import React, { useContext } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Grid,
  MenuItem,
  TextField,
} from '@mui/material';
import { CycleContext } from 'src/context/CycleContext';
import {
  collection,
  getDocs,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from 'src/services/firebase-service';
import logging from 'src/services/logging';

// TODO: manually add new seasons
// don't forget to add new document in hokage collection
const seasons = [
  {
    value: '20252026',
    label: '2026',
  },
  {
    value: '20242025',
    label: '2025',
  },
  {
    value: '20232024',
    label: '2024',
  },
  {
    value: '20222023',
    label: '2023',
  },
  {
    value: '20212022',
    label: '2022',
  },
  {
    value: '20202021',
    label: '2021',
  },
  {
    value: '20182019',
    label: '2019',
  },
];

const rounds = [
  {
    value: 1,
    label: 'Round 1',
  },
  {
    value: 2,
    label: 'Round 2',
  },
  {
    value: 3,
    label: 'Round 3',
  },
  {
    value: 4,
    label: 'Round 4',
  },
];

const roundStatus = [
  {
    value: 0,
    label: 'Closed',
  },
  {
    value: 1,
    label: 'Open',
  },
];

const AppCycleTab = () => {
  const cycle = useContext(CycleContext);

  const handleSeasonChange = async (event: any) => {
    try {
      // first set isCurrentSeason for before season to false
      const currentSeason = cycle.season;
      const q = query(
        collection(db, 'hokage'),
        where('season', '==', currentSeason),
      );
      const snapshot = await getDocs(q);
      const ref = snapshot.docs[0].ref;
      await updateDoc(ref, {
        isCurrentSeason: false,
      });
      // then set isCurrentSeason for after season to true'
      const newSeason = parseInt(event.target.value);
      const q2 = query(
        collection(db, 'hokage'),
        where('season', '==', newSeason),
      );
      const snapshot2 = await getDocs(q2);
      const ref2 = snapshot2.docs[0].ref;
      await updateDoc(ref2, {
        isCurrentSeason: true,
      });
      cycle.setSeason(newSeason);
    } catch (error) {
      logging.error(error, 'AppCycleTab');
    }
  };

  const handleRoundChange = async (event: any) => {
    try {
      const round = event.target.value;
      const q = query(
        collection(db, 'hokage'),
        where('season', '==', cycle.season),
      );
      const snapshot = await getDocs(q);
      const ref = snapshot.docs[0].ref;
      await updateDoc(ref, {
        currentRound: round,
      });
      cycle.setActiveRound(event.target.value);
    } catch (error) {
      logging.error(error, 'AppCycleTab');
    }
  };

  const handleRoundStatusChange = (round: number) => async (event: any) => {
    try {
      const status = event.target.value;
      const q = query(
        collection(db, 'hokage'),
        where('season', '==', cycle.season),
      );
      const snapshot = await getDocs(q);
      const ref = snapshot.docs[0].ref;
      let input;
      switch (round) {
        case 1:
          input = { roundOneStatus: status };
          await updateDoc(ref, input);
          cycle.setRoundOneStatus(status);
          break;
        case 2:
          input = { roundTwoStatus: status };
          await updateDoc(ref, input);
          cycle.setRoundTwoStatus(status);
          break;
        case 3:
          input = { roundThreeStatus: status };
          await updateDoc(ref, input);
          cycle.setRoundThreeStatus(status);
          break;
        case 4:
          input = { roundFourStatus: status };
          await updateDoc(ref, input);
          cycle.setRoundFourStatus(status);
          break;
        default:
          break;
      }
    } catch (error) {
      logging.error(error, 'AppCycleTab');
    }
  };

  return (
    <Card>
      <CardHeader
        title='Current Playoff Cycle'
        titleTypographyProps={{ flex: '1 1 100%', variant: 'h6' }}
      />
      <Divider />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              id='cycle-season'
              select
              fullWidth
              label='Playoff Year'
              value={cycle.season}
              onChange={handleSeasonChange}
            >
              {seasons.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField
              id='cycle-round'
              select
              fullWidth
              label='Active Round'
              value={cycle.activeRound}
              onChange={handleRoundChange}
            >
              {rounds.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <Divider>
              <Chip label='Round Status' />
            </Divider>
          </Grid>
          <Grid item xs={3}>
            <TextField
              id='round-one-status'
              select
              fullWidth
              label='Round 1'
              value={cycle.roundOneStatus}
              onChange={handleRoundStatusChange(1)}
            >
              {roundStatus.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={3}>
            <TextField
              id='round-two-status'
              select
              fullWidth
              label='Round 2'
              value={cycle.roundTwoStatus}
              onChange={handleRoundStatusChange(2)}
            >
              {roundStatus.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={3}>
            <TextField
              id='round-three-status'
              select
              fullWidth
              label='Round 3'
              value={cycle.roundThreeStatus}
              onChange={handleRoundStatusChange(3)}
            >
              {roundStatus.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={3}>
            <TextField
              id='round-four-status'
              select
              fullWidth
              label='Round 4'
              value={cycle.roundFourStatus}
              onChange={handleRoundStatusChange(4)}
            >
              {roundStatus.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default AppCycleTab;
